import React from "react"
import { Toast } from "@base-ui/react/toast"
import Icon from "../icon"
import { VisuallyHidden } from "../visually-hidden"
import { popupTransition } from "../utils/popup-transition"
import { darkPopupSurface, darkPopupArrow } from "../utils/dark-popup"

// =============================================================================
// Manager
// =============================================================================

// A hint is a persistent, programmatically-triggered coachmark that looks like
// a Tooltip (same dark popup and arrow) but is shown imperatively to point at
// and explain a piece of UI (e.g. a newly added control).
//
// It's built on Base UI's Toast rather than a Popover: a coachmark is a
// transient announcement attached to UI, so Toast gives us the live-region
// semantics for free without pretending to be a disclosure widget the user
// toggles. A module-level manager lets a hint be shown from anywhere — even
// outside React — via `Hint.show()`.
const manager = Toast.createToastManager()

type HintSide = "top" | "bottom" | "left" | "right"
type HintAlign = "start" | "center" | "end"

// Per-hint rendering options carried on the toast object's `data`, read back by
// the viewport when it renders each hint.
type HintData = {
  maxWidth: number
  showArrow: boolean
  dismissible: boolean
}

export type HintShowOptions = {
  /** Optional title; sets the hint's accessible name and renders semibold */
  title?: React.ReactNode
  /** Optional body copy; sets the hint's accessible description */
  description?: React.ReactNode
  /** The element to anchor the hint to and point the arrow at */
  anchor?: Element | null
  /** Which side of the anchor the hint appears on */
  position?: HintSide
  /** Alignment of the hint relative to the anchor */
  align?: HintAlign
  /** Gap in pixels between the anchor and hint */
  sideOffset?: number
  /** Maximum width of the hint content in pixels */
  maxWidth?: number
  /** Whether to show the arrow pointing to the anchor */
  showArrow?: boolean
  /** Whether to show the close (X) button */
  dismissible?: boolean
  /**
   * Auto-dismiss the hint after this many milliseconds. Defaults to `0`, which
   * keeps the hint up until it's explicitly dismissed. As with Base UI Toast,
   * the timer pauses while the hint is hovered or focused.
   */
  timeout?: number
  /**
   * Stable id for the hint. Showing a hint with an existing id updates it in
   * place instead of adding a second one.
   */
  id?: string
  /** Called when the hint is dismissed (close button or `Hint.close`) */
  onClose?: () => void
}

/**
 * Show a hint. Returns the hint's id, which can be passed to {@link close}.
 * Requires a {@link HintProvider} mounted somewhere in the tree.
 */
function show({
  title,
  description,
  anchor,
  position = "top",
  align = "center",
  sideOffset = 8,
  maxWidth = 360,
  showArrow = true,
  dismissible = true,
  timeout = 0,
  id,
  onClose
}: HintShowOptions): string {
  return manager.add<HintData>({
    id,
    title,
    description,
    // timeout 0 (the default) persists until dismissed; priority "low" announces
    // politely, not urgently.
    timeout,
    priority: "low",
    onClose,
    positionerProps: {
      anchor: anchor ?? undefined,
      side: position,
      align,
      sideOffset
    },
    data: { maxWidth, showArrow, dismissible }
  })
}

/** Dismiss a hint by id, or all hints when called with no id. */
function close(id?: string) {
  manager.close(id)
}

// =============================================================================
// Components
// =============================================================================

// Mirrors the Tooltip popup, with hint-specific padding/layout on top of the
// shared dark surface.
const popupStyles = ["w-fit p-4", darkPopupSurface, popupTransition()].join(" ")

function HintList() {
  const { toasts } = Toast.useToastManager<HintData>()

  return toasts.map(toast => {
    const {
      maxWidth = 360,
      showArrow = true,
      dismissible = true
    } = toast.data ?? {}

    return (
      <Toast.Positioner
        key={toast.id}
        toast={toast}
        {...toast.positionerProps}
        className="isolate z-[5002]"
      >
        <Toast.Root
          toast={toast}
          // Persistent coachmark: never swipe-to-dismiss.
          swipeDirection={[]}
          data-slot="hint-content"
          className={popupStyles}
          style={{ maxWidth }}
        >
          <Toast.Content className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2 gap-y-1">
            <Toast.Title className="col-start-1 row-start-1 font-semibold leading-6" />
            {dismissible && (
              <Toast.Close className="col-start-2 row-start-1 flex size-6 items-center justify-center rounded text-white/80 outline-2 outline-transparent transition-colors hover:text-white focus-visible:outline-white">
                <VisuallyHidden>Dismiss</VisuallyHidden>
                <Icon name="close" />
              </Toast.Close>
            )}
            <Toast.Description className="col-span-2 leading-6 text-gray-200" />
          </Toast.Content>
          {showArrow && <Toast.Arrow className={darkPopupArrow} />}
        </Toast.Root>
      </Toast.Positioner>
    )
  })
}

export type HintProviderProps = {
  /** The app subtree that can show hints */
  children?: React.ReactNode
}

/**
 * Mount once near the root of the app. Renders the hint surface and wires the
 * shared manager so {@link show} works from anywhere.
 */
export function HintProvider({ children }: HintProviderProps) {
  // Label the landmark via a visually-hidden text node rather than `aria-label`
  // so the label is translatable, and distinct from the general "Notifications"
  // toast region when both live on the page. The label is static and present
  // before any hint, so the live region never announces it.
  const labelId = React.useId()

  return (
    <Toast.Provider toastManager={manager} timeout={0}>
      {children}
      <Toast.Portal>
        <Toast.Viewport aria-labelledby={labelId}>
          <VisuallyHidden id={labelId}>Hints</VisuallyHidden>
          <HintList />
        </Toast.Viewport>
      </Toast.Portal>
    </Toast.Provider>
  )
}

// =============================================================================
// Public API
// =============================================================================

HintProvider.displayName = "Hint.Provider"

export const Hint = {
  Provider: HintProvider,
  show,
  close
} as const
